'use strict';

const Promise = require('bluebird');
const _ = require('lodash');
/* Load modules' code */
const Api = require('./Api');
const Chat = require('./Chat');
const Conf = require('./Conf');
const Connection = require('./Connection');
const Events = require('./Events');
const Helper = require('./Helper');
const Lobby = require('./Lobby');
const Logger = require('./Logger');
const LocalChannel = require('./LocalChannel');
const Diagnoser = require('./Diagnoser');

// execute lodash no conflict to avoid overwiting other global "_" objects
_.noConflict();

/**
 * This is the main module, which loads all the other modules, or just the ones
 * that you specify, as own properties so that they can be used in the client
 * by just referencing ICF.
 */
class ICF {
    /**
     * Don't call it directly, use init instead.
     * Creates an ICF object not initialized.
     * @return {ICF} a new ICF instance
     */
    constructor() {
        this.isInit = false;
        // always load Logger module to have it available even if init fails
        this.Logger = Logger;
        // expose the SDK version to be checked easily
        this.version = process.env.VERSION;
        // save the app's basic settings
        this.settings = {};
        // override setTimeout implementation
        Helper.overrideSetTimeout();
    }

    /**
     * Initializes the ICF object validating the app ID and setting the basic parameters.
     * If this function is not called, none of the modules will be accessible.
     * @param  {Object}  params  User parameters object
     * @return {Promise} A Promise fullfilled with a new ICF object
     */
    init(params = {}) {
        if (this.isInit) {
            return Promise.resolve();
        }
        if (typeof params !== 'object') {
            return Promise.reject({
                message: 'You must provide an object as parameter',
                type: 'init'
            });
        }
        if (!params.appId) {
            return Promise.reject({
                message: 'You must have a registered app ID to use this SDK',
                type: 'init'
            });
        }

        // Set the configuration
        try {
            Conf.set({
                appId: params.appId,
                server: params.server,
                region: params.region,
                setCookieOnDomain: params.setCookieOnDomain,
                setCookieSameSite: params.setCookieSameSite,
                port: params.port,
                isAgent: params.isAgent
            });
        } catch (error) {
            return Promise.reject({
                message: error.message,
                type: 'init'
            });
        }

        this.Logger.configure({
            appId: params.appId,
            version: this.version
        });

        Api.configure(Conf.appId, Conf.server, Conf.apiVersion);

        // load the selected modules if they are specified
        addModules(this, params.modules || {
            all: true
        });

        return Api.request('/apps/' + Conf.appId + '/validate', 'get')
            .then((res) => {
                if (!res.data.isValid) {
                    return Promise.reject({
                        message: 'The app ID provided is not valid',
                        type: 'init'
                    });
                }
                return null;
            })
            .then(() => {
                // get the app basic settings
                return Api.request('/settings', 'get', {
                        appId: Conf.appId
                    })
                    .then((res) => {
                        this.settings = (res.data && res.data.settings) ? res.data.settings : {};
                        return;
                    });
            })
            .then(() => {
                // try to init a previous session if there was any
                return (Connection.isCookieSet()) ?
                    Lobby.init() :
                    null;
            })
            .then(() => {
                this.isInit = true;
                Logger.log('SDK loaded');

                return ICF;
            });
    }

    reset() {
        this.isInit = false;
        Connection.removeToken();
    }
}

/**
 * Includes in the ICF namespace just the selected modules
 * @param {Object} that 'this' context from ICF, so it can actually be extended
 * @param {Object} modulesToAdd The modules to include (set all to load all of them)
 * @private
 */
const addModules = function (that, modulesToAdd) {
    if (modulesToAdd.all || (modulesToAdd.indexOf('Api') !== -1)) {
        that.Api = Api;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Connection') !== -1)) {
        Connection.configure(
            Conf.appId,
            Conf.server, {
                setCookieOnDomain: Conf.setCookieOnDomain,
                isAgent: Conf.isAgent,
                setCookieSameSite: Conf.setCookieSameSite
            },
            'socket',
            that.Api
        );
        that.Connection = Connection;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Events') !== -1)) {
        that.Events = Events;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('LocalChannel') !== -1)) {
        that.LocalChannel = new LocalChannel();
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Chat') !== -1)) {
        // Inject Connection and Lobby as dependencies
        Chat.configure({
            Connection,
            Lobby,
            LocalChannel: that.LocalChannel
        });
        that.Chat = Chat;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Lobby') !== -1)) {
        Lobby.configure({
            LocalChannel: that.LocalChannel
        });
        that.Lobby = Lobby;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Template') !== -1)) {
        that.Template = require('./Template');
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Helper') !== -1)) {
        that.Helper = Helper;
    }
    if (modulesToAdd.all || (modulesToAdd.indexOf('Diagnoser') !== -1)) {
        that.Diagnoser = Diagnoser;
    }
};

module.exports = new ICF();


// WEBPACK FOOTER //
// ./src/ICF.js